import type {
  BookingDetailsParticipant,
  BookingDetailsParticipantExtra,
  BookingDetailsState,
} from '../../domain/types'
import type { ParticipantFullName } from '../utils/participantName'
import type { Locale } from '@/intl/types'
import { formatDate } from '@/scopes/account/common/formatters'
import { formatParticipantAddress } from '../utils/participantAddress'
import { getSupportedParticipantTitle } from '../utils/participantName'

export type { ParticipantFullName } from '../utils/participantName'

interface ParticipantExtraInfo {
  fullName: ParticipantFullName
  birthDate: string
  address: string
  email: string
  phoneNumbers: string[]
  emergencyPhoneNumbers: string[]
  extras: BookingDetailsParticipantExtra[]
  isMainBooker: boolean
  isCancelled: boolean
}

function hasValue(value: string): boolean {
  return value.trim().length > 0
}

function getActiveParticipant(state: BookingDetailsState): BookingDetailsParticipant | undefined {
  return state.activeParticipantId ? state.participants[state.activeParticipantId] : undefined
}

export function getParticipantFullName(participant: Pick<BookingDetailsParticipant, 'title' | 'firstName' | 'lastName'>): ParticipantFullName {
  return {
    titleKey: getSupportedParticipantTitle(participant.title),
    firstName: participant.firstName,
    lastName: participant.lastName,
  }
}

export interface ParticipantsByStatus {
  active: BookingDetailsParticipant[]
  cancelled: BookingDetailsParticipant[]
}

export function getParticipantsByStatus(state: BookingDetailsState): ParticipantsByStatus {
  const active: BookingDetailsParticipant[] = []
  const cancelled: BookingDetailsParticipant[] = []

  for (const participant of Object.values(state.participants)) {
    if (participant.isCancelled) {
      cancelled.push(participant)
    }
    else {
      active.push(participant)
    }
  }

  return { active, cancelled }
}

export function getActiveParticipantExtraInfo(state: BookingDetailsState, locale: Locale): ParticipantExtraInfo {
  const participant = getActiveParticipant(state)
  const formattedAddress = participant ? formatParticipantAddress(participant.address) : ''
  const rawBirthDate = participant?.birthDate ?? ''
  const formattedBirthDate = formatDate(rawBirthDate, locale, 'long')

  return {
    fullName: participant
      ? getParticipantFullName(participant)
      : { titleKey: null, firstName: '-', lastName: '' },
    birthDate: formattedBirthDate || rawBirthDate,
    address: formattedAddress,
    email: participant?.email ?? '',
    phoneNumbers: participant?.phoneNumbers ?? [],
    emergencyPhoneNumbers: participant?.emergencyPhoneNumbers ?? [],
    extras: (participant?.extras ?? []).filter(extra => extra.descriptions.length > 0),
    isMainBooker: participant?.type === 'MainBooker',
    isCancelled: participant?.isCancelled ?? false,
  }
}

export function getIsEditableParticipant(state: BookingDetailsState): boolean {
  if (!state.isSelfServiceEnabled) {
    return false
  }

  const participant = getActiveParticipant(state)
  if (!participant || participant.isCancelled) {
    return false
  }

  const hasAddress = hasValue(formatParticipantAddress(participant.address))
  const hasBirthDate = hasValue(participant.birthDate)
  const hasEmail = hasValue(participant.email)
  const hasPhoneNumber = participant.phoneNumbers.length > 0
  const hasEmergencyPhoneNumber = participant.emergencyPhoneNumbers.length > 0

  return hasBirthDate || hasEmail || hasAddress || hasPhoneNumber || hasEmergencyPhoneNumber
}
